import React from 'react';
import { Link } from 'react-router-dom';
import { DynamicIcon } from '../components/icons/DynamicIcon';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <DynamicIcon name="shield" size={32} className="text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Política de Privacidade</h1>
            </div>
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <DynamicIcon name="arrowLeft" size={16} className="mr-2" />
              Voltar
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 sm:p-8">
          <div className="mb-8 text-center">
            <p className="text-sm text-gray-500">Última atualização: 29 de junho de 2025</p>
          </div>

          <div className="prose prose-blue max-w-none">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. INTRODUÇÃO</h2>
            
            <p className="text-gray-700">
              A EventPour ("nós", "nosso" ou "empresa") está comprometida em proteger sua privacidade. Esta Política de Privacidade explica como coletamos, usamos, divulgamos e protegemos suas informações pessoais quando você utiliza nossa plataforma, aplicativo e serviços relacionados (coletivamente, os "Serviços").
            </p>
            
            <p className="text-gray-700">
              Ao utilizar nossos Serviços, você concorda com a coleta e uso de informações de acordo com esta política. Processamos seus dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD) e outras leis de privacidade aplicáveis.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. INFORMAÇÕES QUE COLETAMOS</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">2.1 Informações que você nos fornece</h3>
            <p className="text-gray-700">Podemos coletar os seguintes tipos de informações:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Informações de cadastro:</strong> nome, endereço de e-mail, número de telefone, endereço comercial.</li>
              <li><strong>Informações de perfil:</strong> foto, biografia, informações profissionais.</li>
              <li><strong>Informações de pagamento:</strong> dados de cartão de crédito, informações de faturamento.</li>
              <li><strong>Conteúdo do usuário:</strong> orçamentos, catálogos de bebidas, informações de clientes, eventos.</li>
              <li><strong>Comunicações:</strong> mensagens enviadas através de nossos canais de suporte.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">2.2 Informações coletadas automaticamente</h3>
            <p className="text-gray-700">Quando você utiliza nossos Serviços, podemos coletar automaticamente:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Dados de uso:</strong> como você interage com nossos Serviços, páginas visitadas, recursos utilizados.</li>
              <li><strong>Informações do dispositivo:</strong> tipo de dispositivo, sistema operacional, navegador, idioma.</li>
              <li><strong>Dados de localização:</strong> localização aproximada baseada no endereço IP.</li>
              <li><strong>Cookies e tecnologias similares:</strong> conforme detalhado em nossa Política de Cookies.</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. COMO USAMOS SUAS INFORMAÇÕES</h2>
            
            <p className="text-gray-700">Utilizamos suas informações para os seguintes propósitos:</p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Fornecer e melhorar nossos Serviços:</strong> operar, manter e aprimorar a plataforma EventPour.</li>
              <li><strong>Personalização:</strong> adaptar nossos Serviços às suas preferências e necessidades.</li>
              <li><strong>Comunicação:</strong> enviar notificações, atualizações e informações relacionadas ao serviço.</li>
              <li><strong>Suporte ao cliente:</strong> responder a suas solicitações e fornecer assistência.</li>
              <li><strong>Processamento de pagamentos:</strong> cobrar por assinaturas e serviços premium.</li>
              <li><strong>Segurança:</strong> proteger nossos Serviços contra fraudes e atividades não autorizadas.</li>
              <li><strong>Análise e pesquisa:</strong> entender como nossos Serviços são utilizados para melhorá-los.</li>
              <li><strong>Marketing:</strong> enviar comunicações de marketing, sujeitas às suas preferências e consentimento.</li>
              <li><strong>Conformidade legal:</strong> cumprir obrigações legais e regulatórias aplicáveis.</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. COMPARTILHAMENTO DE INFORMAÇÕES</h2>
            
            <p className="text-gray-700">Podemos compartilhar suas informações nas seguintes circunstâncias:</p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.1 Provedores de serviços</h3>
            <p className="text-gray-700">
              Compartilhamos informações com terceiros que nos auxiliam na operação dos Serviços, como processadores de pagamento, serviços de hospedagem e análise de dados. Esses provedores têm acesso limitado às suas informações e são contratualmente obrigados a usá-las apenas para os fins específicos acordados.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.2 Conformidade legal e proteção</h3>
            <p className="text-gray-700">
              Podemos divulgar suas informações quando acreditarmos, de boa-fé, que a divulgação é necessária para:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Cumprir uma obrigação legal, regulatória ou processo legal.</li>
              <li>Proteger os direitos, propriedade ou segurança da EventPour, nossos usuários ou o público.</li>
              <li>Detectar, prevenir ou investigar fraudes, abusos ou atividades ilegais.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.3 Transações comerciais</h3>
            <p className="text-gray-700">
              Se a EventPour estiver envolvida em uma fusão, aquisição ou venda de ativos, suas informações podem ser transferidas como parte dessa transação. Notificaremos você sobre qualquer mudança de propriedade ou uso de suas informações pessoais.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.4 Com seu consentimento</h3>
            <p className="text-gray-700">
              Podemos compartilhar suas informações com terceiros quando você nos autorizar expressamente a fazê-lo.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. SEUS DIREITOS E ESCOLHAS</h2>
            
            <p className="text-gray-700">De acordo com a LGPD e outras leis aplicáveis, você tem os seguintes direitos:</p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Acesso:</strong> solicitar acesso às suas informações pessoais que processamos.</li>
              <li><strong>Correção:</strong> solicitar a correção de informações imprecisas ou incompletas.</li>
              <li><strong>Exclusão:</strong> solicitar a exclusão de suas informações pessoais em determinadas circunstâncias.</li>
              <li><strong>Restrição:</strong> solicitar a restrição do processamento de suas informações pessoais.</li>
              <li><strong>Portabilidade:</strong> receber suas informações pessoais em formato estruturado e transferível.</li>
              <li><strong>Oposição:</strong> opor-se ao processamento de suas informações pessoais em determinadas circunstâncias.</li>
              <li><strong>Retirada de consentimento:</strong> retirar qualquer consentimento previamente fornecido.</li>
            </ul>
            
            <p className="text-gray-700 mt-4">
              Para exercer esses direitos, entre em contato conosco em privacy@eventpour.com.br. Responderemos à sua solicitação dentro do prazo legal aplicável.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. SEGURANÇA DE DADOS</h2>
            
            <p className="text-gray-700">
              Implementamos medidas técnicas e organizacionais apropriadas para proteger suas informações pessoais contra acesso não autorizado, perda acidental, alteração ou divulgação. Essas medidas incluem:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Criptografia de dados em trânsito e em repouso</li>
              <li>Controles de acesso rigorosos</li>
              <li>Monitoramento de segurança contínuo</li>
              <li>Backups regulares</li>
              <li>Treinamento de segurança para funcionários</li>
            </ul>
            
            <p className="text-gray-700 mt-4">
              Embora nos esforcemos para proteger suas informações, nenhum método de transmissão pela internet ou armazenamento eletrônico é 100% seguro. Portanto, não podemos garantir segurança absoluta.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. RETENÇÃO DE DADOS</h2>
            
            <p className="text-gray-700">
              Mantemos suas informações pessoais pelo tempo necessário para cumprir os propósitos descritos nesta Política de Privacidade, a menos que um período de retenção mais longo seja exigido ou permitido por lei. Os critérios usados para determinar nossos períodos de retenção incluem:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>O período durante o qual temos um relacionamento ativo com você</li>
              <li>Obrigações legais às quais estamos sujeitos</li>
              <li>Prazos de prescrição aplicáveis, litígios ou investigações regulatórias</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">8. TRANSFERÊNCIAS INTERNACIONAIS</h2>
            
            <p className="text-gray-700">
              Suas informações pessoais podem ser transferidas e processadas em países diferentes daquele em que você reside. Esses países podem ter leis de proteção de dados diferentes das leis do seu país.
            </p>
            
            <p className="text-gray-700 mt-4">
              Quando transferimos suas informações pessoais para outros países, tomamos medidas para garantir que elas recebam um nível adequado de proteção, incluindo a implementação de cláusulas contratuais padrão aprovadas pela autoridade de proteção de dados competente.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">9. COOKIES E TECNOLOGIAS SIMILARES</h2>
            
            <p className="text-gray-700">
              Utilizamos cookies e tecnologias similares para coletar informações sobre como você interage com nossos Serviços. Isso nos ajuda a melhorar sua experiência, personalizar conteúdo e analisar o uso da plataforma. 
            </p>
            
            <p className="text-gray-700 mt-4">
              Você pode gerenciar suas preferências de cookies através do nosso banner de cookies ou painel de preferências. Para mais informações, consulte nossa <a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Política de Cookies</a>.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">10. PRIVACIDADE DE CRIANÇAS</h2>
            
            <p className="text-gray-700">
              Nossos Serviços não são direcionados a pessoas menores de 18 anos, e não coletamos intencionalmente informações pessoais de crianças. Se tomarmos conhecimento de que coletamos informações pessoais de uma criança sem o consentimento parental, tomaremos medidas para remover essas informações.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">11. ALTERAÇÕES NESTA POLÍTICA</h2>
            
            <p className="text-gray-700">
              Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou por outros motivos operacionais, legais ou regulatórios. Notificaremos você sobre quaisquer alterações materiais através de um aviso em nosso site ou por e-mail antes que as alterações entrem em vigor.
            </p>
            
            <p className="text-gray-700 mt-4">
              Recomendamos que você revise esta política regularmente para estar informado sobre como protegemos suas informações.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">12. CONTATO</h2>
            
            <p className="text-gray-700">
              Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade ou ao processamento de suas informações pessoais, entre em contato com nosso Encarregado de Proteção de Dados:
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700">
                <strong>E-mail:</strong> privacy@eventpour.com.br<br />
                <strong>Endereço:</strong> [Endereço completo]<br />
                <strong>Telefone:</strong> [Número de telefone]
              </p>
            </div>
            
            <p className="text-gray-700 mt-4">
              Responderemos à sua solicitação dentro do prazo legal aplicável.
            </p>

            {/* Seção final */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-center text-gray-700 font-semibold">
                EventPour Tecnologia Ltda.<br />
                CNPJ: XX.XXX.XXX/0001-XX<br />
                Email: privacy@eventpour.com.br
              </p>
              
              <p className="text-center text-gray-600 mt-4 italic">
                Esta Política de Privacidade foi atualizada pela última vez em 29 de junho de 2025.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <DynamicIcon name="glasses" size={24} className="text-blue-600 mr-2" />
              <span className="font-bold text-gray-900">EventPour</span>
            </div>
            
            <div className="flex space-x-6 text-sm">
              <span className="text-gray-900 font-medium">Política de Privacidade</span>
              <a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900">Termos de Uso</a>
              <a href="#" className="text-gray-600 hover:text-gray-900">Contato</a>
            </div>
          </div>
          
          <div className="mt-8 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} EventPour. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;